import type {
  DisbursementAcademicTerm,
  DisbursementApprovalTrackEntry,
  DisbursementDepartment,
  DisbursementPath,
  DisbursementStatusCode,
  DisbursementUser,
} from './disbursement.model';

// v2.0 phase 2 — the รอบส่งเบิก is the approvable, single backward edge at การเงิน's send-back — context/reference/GLOSSARY_Disbursement.md §3
export type DisbursementRoundStatusCode =
  | 'draft'
  | 'pending_finance_sign'
  | 'pending_head'
  | 'pending_vice_dean_admin'
  | 'completed'
  | 'returned_to_officer'
  | 'cancelled';

export const DISBURSEMENT_ROUND_STATUS_LABELS: Record<DisbursementRoundStatusCode, string> = {
  draft: 'แบบร่าง',
  pending_finance_sign: 'รอเจ้าหน้าที่การเงินเห็นชอบ',
  pending_head: 'รอหัวหน้าสาขาขอเบิก',
  pending_vice_dean_admin: 'รอรองคณบดีฝ่ายบริหารอนุมัติ',
  completed: 'เสร็จสิ้น',
  returned_to_officer: 'ส่งกลับให้เจ้าหน้าที่สารบรรณแก้ไข',
  cancelled: 'ยกเลิกเนื่องจากการแก้ไขตารางสอน',
};

// Mirrors DisbursementCoverMemo::EDITABLE_STATUS_CODES — the only states in which
// สารบรรณ may still change เลขที่/วันที่/สมาชิกรอบ.
export const DISBURSEMENT_ROUND_EDITABLE_STATUS_CODES: DisbursementRoundStatusCode[] = [
  'draft',
  'returned_to_officer',
];

export const DISBURSEMENT_ROUND_FINAL_STATUS_CODE: DisbursementRoundStatusCode = 'completed';

// ADR-CE: round artifacts build async, pages poll rows at this cadence — context/decisions/DECISION_DisbursementV20.md
export const ROUND_DOC_POLL_INTERVAL_MS = 10_000;

// ADR-CG: check round_merged_fresh, not has_round_merged — context/decisions/DECISION_DisbursementV20.md
export function roundArtifactsPending(
  round: Pick<DisbursementRoundModel, 'round_merged_fresh' | 'export_ready' | 'status_code'>,
): boolean {
  if (round.status_code === 'cancelled') {
    return false;
  }

  return !round.round_merged_fresh || (round.status_code === 'completed' && !round.export_ready);
}

// One member certification of a round, as the rounds controller renders it.
export interface DisbursementRoundMember {
  id: number;
  label: string;
  amount: number;
  status_code: DisbursementStatusCode | null;
}

// GET /disbursement_rounds/:id/breakdown — ยอดของรอบแจกแจงเป็นรายอาจารย์ (นานาชาติเท่านั้น).
// เบิกรายวิชา 1 ใบรับรอง = 1 วิชา/section อาจารย์คนเดียวจึงถูกรวมข้ามใบมาแล้วจาก backend
// (Disbursement::Rounds::TeacherBreakdown) — แถวและบรรทัดย่อยที่เป็น ฿0 ถูกตัดออกตั้งแต่ต้นทาง
export interface DisbursementRoundBreakdownItem {
  subject_code: string | null;
  subject_name: string | null;
  section: string | null;
  amount: number;
}

export interface DisbursementRoundBreakdownTeacher {
  user_id: number;
  full_name: string | null;
  amount: number;
  items: DisbursementRoundBreakdownItem[];
}

export interface DisbursementRoundBreakdown {
  teachers: DisbursementRoundBreakdownTeacher[];
  total: number;
  // ยอดของใบรับรองที่แจกแจงรายบุคคลไม่ได้ (snapshot ไม่มี teachers[]) — ปกติ 0
  unattributed_total: number;
}

export interface DisbursementRoundModel {
  id: number;
  academic_term: DisbursementAcademicTerm | null;
  department: DisbursementDepartment | null;
  path: DisbursementPath;
  path_label: string;
  document_number: string | null;
  issued_on: string | null;
  issued_by: DisbursementUser | null;
  // รอบที่ n of this (term, department, path) — precomputed server-side, never a client count.
  round_no: number | null;
  teacher_count: number;
  member_count: number;
  total_amount: number | null;
  // Set only when the round has exactly one teacher (shown instead of the count).
  single_teacher_name: string | null;
  status_code: DisbursementRoundStatusCode | null;
  status_label: string | null;
  // locked = round completed; editable = draft|returned_to_officer.
  locked: boolean;
  editable: boolean;
  submitted_at: string | null;
  finance_signed_at: string | null;
  head_signed_at: string | null;
  vice_dean_signed_at: string | null;
  completed_at: string | null;
  returned_at: string | null;
  send_back_comment: string | null;
  send_back_by: DisbursementUser | null;
  // Informational from v2.0 on — the first registry download, not a terminal signal.
  exported_at: string | null;
  window_closed: boolean;
  has_pdf: boolean;
  has_round_merged: boolean;
  // ADR-CG: presence AND freshness vs the ใบปะหน้า's last render — context/decisions/DECISION_DisbursementV20.md
  round_merged_fresh: boolean;
  // v2.0 — the Export gate: the stored round_merged exists AND was rendered after the last
  // ใบปะหน้า signature, so the bundle the officer downloads already carries that signature.
  // A round that just completed reads false until the re-render job finishes.
  export_ready: boolean;
  // Four-station stepper (Disbursement::Rounds::StationResolver), same shape as phase 1's.
  approval_track: DisbursementApprovalTrackEntry[];
  certifications: DisbursementRoundMember[];
}

export interface DisbursementRoundListParams {
  academic_term_id?: number | string | null;
  department_id?: number | string | null;
  path?: DisbursementPath | null;
  status?: DisbursementRoundStatusCode | null;
  keywords?: string | null;
  page?: number;
  limit?: number;
}

export interface DisbursementRoundListResponse {
  data: DisbursementRoundModel[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

// การเงิน's send-back — the one backward edge, comment required.
export interface DisbursementRoundSendBackPayload {
  comment: string;
}
